import React, { useEffect, useState, useMemo } from 'react';
import { RESET_PASSWORD } from 'services/dataService/resources';
import dataService from 'services/dataService';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useHistory } from 'react-router-dom';
import withLoader from 'components/organisms/withLoader';
import Button from 'components/atoms/button';
import CallIcon from 'components/atoms/icons/callIcon';
import ControlPanelButton from 'components/atoms/controlPanelButton';
import forms from 'constants/forms';
import { showResult, getPatientStatus, scrollToErrorIfExist } from 'utils';
import {
  isPractice,
  isCcmAgencyDoctor,
  isAdmin,
  isAgencyDoctor,
  isRoleNameAdministrator,
  isPatientAcceptance,
} from 'utils/userTypeHelper';
import useUser from 'utils/useUser';
import { Formik, Form } from 'formik';
import { addDragListener } from 'utils/dragListener';
import { Card, CardBody, Col, Row, Table } from 'reactstrap';
import DxCodes from 'components/molecules/adminUsers/dxCodes';
import Clinic from 'components/molecules/adminUsers/clinic';
import Physician from 'components/molecules/adminUsers/physician';
import Insurances from 'components/molecules/adminUsers/insurances';
import Devices from 'components/molecules/adminUsers/devices';
import { hasPatientAcceptanceRole } from 'utils/accessManager/helpers';
import ResetPasswordIcon from 'components/atoms/icons/resetPassword.jsx';
import { isMobile } from 'utils/deviceHelper';
import CareAgent from './components/careAgent';
import PersonalInfo from './components/personalInfo';
import AccountInfo from './components/accountInfo';
import ContactInfo from './components/contactInfo';
import Emr from './components/emr';
import PriorAuth from './components/priorAuth';
import Thresholds from './components/thresholds';
import {
  formatDevices,
  reformatReadingTypes,
} from '../../../../utils/patientDeviceStatus';
import patientSchema from './schema';
import Input from '../../../atoms/input';
import ClinicalNotes from '../../ccm/patientDetails/patientGoals/conditions';

const CARE_AGENTS = forms.adminPanelsUser.userEdit.careAgents;
const PAT_MANAGER = CARE_AGENTS[0];
const ONBOARDING_MANAGER = CARE_AGENTS[1];
const CCM_PAT_MANAGER = CARE_AGENTS[2];
const CCM_ADMISSION_MANAGER = CARE_AGENTS[3];
const CCM_MANAGER = CARE_AGENTS[4];

const getAgentGuid = (agents, type) => {
  const selectedAgent = agents.find((agent) => agent.type === type);

  if (selectedAgent) {
    return selectedAgent.careAgentGuid;
  }
};

const EditPatient = () => {
  const { patientGuid } = useParams();
  const { userType, roleNames } = useUser();
  const iamPatientAcceptance = useMemo(() =>
    hasPatientAcceptanceRole(roleNames),
  );
  const canEditCareAgents = useMemo(
    () =>
      (isAgencyDoctor(userType) && isRoleNameAdministrator(roleNames)) ||
      (isAgencyDoctor(userType) && isPatientAcceptance(roleNames)) ||
      isAdmin(userType),
  );
  const clinics = useSelector((state) => state.clinics.data);
  const doctors = useSelector((state) => state.adminPanelPatients.doctors);
  const patient = useSelector((state) => state.adminPanelPatients.patient);
  const careAgentOptions = useSelector(
    (state) => state.adminPanelPatients.careAgentOptions,
  );
  const history = useHistory();
  const device = useSelector((state) => state.user.deviceType);

  const { getPatient: patientDetailsLoading, editPatient: editPatientLoading } =
    useSelector((state) => state.loading.effects.adminPanelPatients);
  const [initialDoctorsLoading, setInitialDoctorsLoading] = useState(false);

  const {
    clinics: { getClinics },
    adminPanelPatients: { getDoctors, getPatient, editPatient, getCareAgents },
    connect: { handleConnectPopoutOpen, connectDetails },
  } = useDispatch();

  const onClickConnect = (isRpmCall) => {
    const details = {
      phone: patient.phone,
      guid: patient.guid,
      isRpmCall,
    };
    connectDetails(details);
    handleConnectPopoutOpen(true);
    addDragListener('connect-modal', 'connect-modal-header');
  };

  const resetPassword = async (email) => {
    await dataService.createList(RESET_PASSWORD, { email });
  };

  const initialLoad = async () => {
    setInitialDoctorsLoading(true);
    await getPatient({ patientGuid });
    const promises = [
      getClinics(),
      getDoctors({ clinicGuid: patient.clinic?.clinicGuid }),
    ];
    if (canEditCareAgents) promises.push(getCareAgents());
    await Promise.all(promises);
    setInitialDoctorsLoading(false);
  };

  useEffect(() => {
    initialLoad().then();
  }, []);

  const processEmr = (values) => {
    const emr = {
      name: values.emrName && values.emrName?.trim(),
      account: values.account,
      emrId1: values.emrId1 && values.emrId1?.trim(),
      emrId2: values.emrId2 && values.emrId2?.trim(),
      readingsFlag: values.readingsFlag,
      reports: values.reports && values.reports?.trim(),
      billing: values.billing,
      billingCycle: values.billingCycle && values.billingCycle?.trim(),
      supervisingProviderId:
        values.supervisingProviderId && values.supervisingProviderId?.trim(),
      renderingProviderId:
        values.renderingProviderId && values.renderingProviderId?.trim(),
    };
    if (Object.values(emr).every((value) => !value)) {
      return undefined;
    }
    return emr;
  };

  const processDiagnosis = (values) => {
    const diagnosis = {
      bloodGlucose: values.bloodGlucose && values.bloodGlucose?.trim(),
      bloodPressure: values.bloodPressure && values.bloodPressure?.trim(),
      weight: values.weight && values.weight?.trim(),
      pulmonary: values.pulmonary && values.pulmonary?.trim(),
      sleep: values.sleep && values.sleep?.trim(),
      allCodes: values.allCodes && values.allCodes?.trim(),
    };
    if (Object.values(diagnosis).every((value) => !value)) {
      return undefined;
    }
    return diagnosis;
  };

  const handleSubmit = async (values) => {
    const devicesFormatted = values.devices.map((e) => ({
      ...e,
      readingTypes: formatDevices(e.readingTypes || []),
    }));

    let emergencyContactFirstName = '';
    let emergencyContactLastName = '';
    if (values.emergencyContact?.trim()) {
      const [emFirstName, emLastName] = values.emergencyContact
        ?.trim()
        .split(' ');
      emergencyContactFirstName = emFirstName;
      emergencyContactLastName = emLastName;
    }

    const postData = {
      patientGuid,
      patientInfo: {
        title: values.title,
        middleName: values.middleName ? values.middleName?.trim() : null,
        firstName: values.firstName?.trim(),
        lastName: values.lastName?.trim(),
        deviceOrderSpecifications: values.deviceOrderSpecifications?.trim(),
        birthDate: values.birthDate?.trim(),
        primaryNumber: values.phone?.trim(),
        secondaryNumber: values.homeNumber?.trim(),
        email: values.email?.trim(),
        gender: values.gender,
        race: values.race,
        martialStatus: values.martialStatus,
        language: values.language?.trim(),
        workingStatus: values.workingStatus?.trim(),
        ethnicity: values.ethnicity?.trim(),
        status: values.status?.trim(),
        ccmStatus: values.ccmStatus,
        pcmStatus: values.pcmStatus,
        address: values.address?.trim(),
        timezone: values.timezone,
        state: values.state?.trim(),
        city: values.city?.trim(),
        preferredContactMethod: values.preferredContactMethod?.trim(),
        auth0Enabled: values.auth0Enabled,
        zipPostalCode: values.zipPostalCode?.trim(),
        emergencyContact: {
          emergencyContactFirstName: emergencyContactFirstName?.trim(),
          emergencyContactLastName: emergencyContactLastName?.trim(),
          emergencyContactRelationship:
            values.emergencyContactRelationship?.trim() || '',
          emergencyContactNumber: values.emergencyContactNumber?.trim() || '',
        },
      },
      diagnosisCodes: processDiagnosis(values),
      clinicGuid: values.clinicGuid,
      doctorGuid: values.doctorGuid,
      devices: devicesFormatted?.map((item) => ({
        ...item,
        manufacturer: item.manufacturer?.trim(),
        deviceId: item.deviceId?.trim(),
      })),
      insurances: values.insurances?.map((item) => ({
        ...item,
        number: item.number?.trim(),
        planName: item.planName?.trim(),
      })),
      thresholds: {
        maxCriticalSystolic: values.maxCriticalSystolic,
        minCriticalSystolic: values.minCriticalSystolic,
        maxCriticalDiastolic: values.maxCriticalDiastolic,
        minCriticalDiastolic: values.minCriticalDiastolic,
        maxCriticalPulse: values.maxCriticalPulse,
        minCriticalPulse: values.minCriticalPulse,
        maxCriticalSleepPulse: values.maxCriticalSleepPulse,
        minCriticalSleepPulse: values.minCriticalSleepPulse,
        maxCriticalGlucose: values.maxCriticalGlucose,
        minCriticalGlucose: values.minCriticalGlucose,
        maxCriticalWeight: values.maxCriticalWeight,
        minCriticalWeight: values.maxCriticalWeight,
        baseLineWeight: values.baseLineWeight ? values.baseLineWeight : null,
        maxRiskSystolic: values.maxRiskSystolic,
        minRiskSystolic: values.minRiskSystolic,
        maxRiskDiastolic: values.maxRiskDiastolic,
        minRiskDiastolic: values.minRiskDiastolic,
        maxRiskPulse: values.maxRiskPulse,
        minRiskPulse: values.minRiskPulse,
        maxRiskSleepPulse: values.maxRiskSleepPulse,
        minRiskSleepPulse: values.minRiskSleepPulse,
        maxRiskGlucose: values.maxRiskGlucose,
        minRiskGlucose: values.minRiskGlucose,
        maxRiskWeight: values.maxRiskWeight,
        minRiskWeight: values.maxRiskWeight,
        criticalBloodoxygen: values.criticalBloodoxygen,
        riskBloodoxygen: values.riskBloodoxygen,
        minRiskPef: values.minRiskPef,
        minCriticalPef: values.minCriticalPef,
        minCriticalFev1: values.minCriticalFev1,
      },
      emr: processEmr(values),
      priorAuths: values.priorAuths,
      careAgents: [],
    };

    const normalizeCareAgents = (managerGuid, type) => {
      const careAgentData = {
        doctorGuid: managerGuid,
        type,
      };

      postData.careAgents.push(careAgentData);
    };

    [
      {
        value: values.patManager,
        type: PAT_MANAGER.id,
      },
      {
        value: values.onboardingManager,
        type: ONBOARDING_MANAGER.id,
      },
      {
        value: values.ccmManager,
        type: CCM_MANAGER.id,
      },
      {
        value: values.ccmPatManager,
        type: CCM_PAT_MANAGER.id,
      },
      {
        value: values.ccmAdmissionManager,
        type: CCM_ADMISSION_MANAGER.id,
      },
    ].forEach(({ value, type }) => normalizeCareAgents(value, type));

    const inData = {
      postData,
      patientGuid,
    };
    const { data, error } = await editPatient(inData);
    if (data && data.attachedDevices.length > 0) {
      const ids = data.attachedDevices
        .map((attachedDevice) => attachedDevice.deviceId)
        .join(', ');
      showResult(
        `Warning: Update succeed, but devices with ID's: ${ids} not added!`,
      );
    } else {
      showResult(error);
    }
    if (data) {
      history.push(`/admin/patient/${patientGuid}`);
    }
  };
  let statusLabel = '';
  if (patient.profile) {
    const { label } = getPatientStatus(patient.profile.status);
    statusLabel = label;
  }
  let formattedDevices = [];
  if (patient.devices) {
    formattedDevices = patient.devices.map((el) => {
      const readingTypes = reformatReadingTypes(el.readingTypes || []);
      return {
        ...el,
        readingTypes: forms.readingTypes.filter((item) =>
          readingTypes.includes(item.id),
        ),
      };
    });
  }

  return (
    <div className="admin-patient">
      {withLoader(
        patientDetailsLoading || initialDoctorsLoading,
        <Card className="card-border">
          <CardBody>
            <div className="admin-patient-header admin-edit-patient__header__wrapper">
              <span>Edit Patient Info</span>
              <div className="admin-patient-header-actions">
                <div className="admin-patient-header-actions-call">
                  <ControlPanelButton
                    label="Send Password Reset Email"
                    icon={ResetPasswordIcon}
                    handleClick={() => resetPassword(patient.profile.email)}
                  />
                </div>
                {!isMobile(device) && (
                  <>
                    <div className="admin-patient-header-actions-call">
                      <ControlPanelButton
                        label="Call Patient Primary Number"
                        icon={CallIcon}
                        handleClick={() => onClickConnect(true)}
                      />
                    </div>
                    {!iamPatientAcceptance &&
                      (isCcmAgencyDoctor(userType, roleNames) ||
                        isPractice(userType) ||
                        isAdmin(userType)) && (
                        <div className="admin-patient-header-actions-call">
                          <ControlPanelButton
                            label="Call CCM Patient Primary Number"
                            icon={CallIcon}
                            handleClick={() => onClickConnect(false)}
                          />
                        </div>
                      )}
                  </>
                )}
                <div className="admin-patient-header-actions-status">
                  {statusLabel}
                </div>
              </div>
            </div>
            <Formik
              onSubmit={handleSubmit}
              initialValues={{
                ...patient.clinic,
                ...patient.doctor,
                ...patient.profile,
                ...patient.thresholds,
                supervisingProviderId: patient.emr?.supervisingProviderId,
                emrName: patient.emr?.name,
                ...patient.emr,
                ...patient.diagnosis,
                insurances: patient.insurances ? [...patient.insurances] : [],
                priorAuths: patient.priorAuths ? [...patient.priorAuths] : [],
                devices: formattedDevices,
                emergencyContact: `${
                  patient.profile?.emergencyContactFirstName || ''
                } ${patient.profile?.emergencyContactLastName || ''}`,
                authorizationNumber: patient?.priorAuths?.length
                  ? patient?.priorAuths[0]?.authorizationNumber
                  : undefined,
                priorAuthStartDate: patient?.priorAuths?.length
                  ? patient?.priorAuths[0]?.startDate
                  : undefined,
                priorAuthEndDate: patient?.priorAuths?.length
                  ? patient?.priorAuths[0]?.endDate
                  : undefined,
                patManager: patient.careAgents.length
                  ? getAgentGuid(patient.careAgents, PAT_MANAGER.id)
                  : undefined,
                onboardingManager:
                  patient.careAgents.length > 0
                    ? getAgentGuid(patient.careAgents, ONBOARDING_MANAGER.id)
                    : undefined,
                ccmPatManager:
                  patient.careAgents.length > 0
                    ? getAgentGuid(patient.careAgents, CCM_PAT_MANAGER.id)
                    : undefined,
                ccmAdmissionManager:
                  patient.careAgents.length > 0
                    ? getAgentGuid(patient.careAgents, CCM_ADMISSION_MANAGER.id)
                    : undefined,
                ccmManager:
                  patient.careAgents.length > 0
                    ? getAgentGuid(patient.careAgents, CCM_MANAGER.id)
                    : undefined,
              }}
              validationSchema={patientSchema}
            >
              {({ errors, values, setFieldValue }) => (
                <Form>
                  <div className="admin-patient-edit-form">
                    <Table>
                      <tbody>
                        <tr>
                          <td>
                            <PersonalInfo
                              setFieldValue={setFieldValue}
                              errors={errors}
                              values={values}
                            />
                            <AccountInfo
                              guid={patientGuid}
                              iamPatientAcceptance={iamPatientAcceptance}
                            />
                          </td>
                          <td>
                            <ContactInfo errors={errors} />
                          </td>
                        </tr>
                        <tr>
                          <td>
                            <span>Other Info</span>
                            <DxCodes errors={errors} />
                            <Emr
                              errors={errors}
                              setFieldValue={setFieldValue}
                              values={values}
                            />
                            <PriorAuth values={values} errors={errors} />
                          </td>
                          <td>
                            <Clinic
                              errors={errors}
                              clinics={clinics}
                              getDoctors={getDoctors}
                              patient={patient}
                            />
                            <Physician
                              errors={errors}
                              doctors={doctors}
                              patient={patient}
                              values={values}
                            />
                            <CareAgent
                              errors={errors}
                              selectedAgents={patient.careAgents}
                              agentOptions={careAgentOptions}
                            />
                          </td>
                        </tr>
                        <tr>
                          <td>
                            <Insurances errors={errors} values={values} />
                            <Devices errors={errors} values={values} />
                            <div className="admin-patient-form-block">
                              <span>Device Order Specifications</span>
                              <Row>
                                <Col sm="7" md="7" lg="7">
                                  <Input
                                    label="Device Order Specifications"
                                    id="deviceOrderSpecifications"
                                    type="text"
                                  />
                                </Col>
                              </Row>
                            </div>
                            <ClinicalNotes isAdminPanel makeInitialRequest />
                          </td>
                          <td>
                            <Thresholds errors={errors} />
                          </td>
                        </tr>
                      </tbody>
                    </Table>
                  </div>
                  <div className="text-center">
                    <Button
                      type="submit"
                      text="Save"
                      data-cy="admin-patient-edit__save-btn"
                      disabled={editPatientLoading}
                      handleClick={scrollToErrorIfExist}
                    />
                  </div>
                </Form>
              )}
            </Formik>
          </CardBody>
        </Card>,
      )}
    </div>
  );
};

export default EditPatient;
